// @flow
import React, { useState, useRef, useEffect, ReactElement } from "react";
import classNames from "classnames";

import Styles from "./Radio.module.scss";
import { useTheme } from "../../styles/useTheme";
import { useTranslation } from "react-i18next";

type RadioGroupProps = {
  children: React.ReactNode;
  fullWidth?: boolean;
  centered?: boolean;
  direction?: "row" | "column";
  className?: string;
  required?: boolean;
  title?: string | React.ReactNode;
  onValidate?: (error: string | undefined) => void;
};

const RadioGroup = ({
  children,
  fullWidth,
  centered,
  direction = "row",
  className,
  title,
  required,
  onValidate,
}: RadioGroupProps) => {
  const [error, setError] = useState<string | undefined>("");
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (ref.current && required) {
      // If the focus is not on the radio group
      if (!ref.current?.contains(e.relatedTarget as HTMLInputElement)) {
        // Gets all radio buttons in the group
        const radios = ref.current.querySelectorAll<HTMLInputElement>("input[type=radio]");
        const checked = Array.from(radios).some((radio) => radio.checked);
        // If no radio is checked, set error
        if (!checked) {
          setError(t("validations.required") || undefined);
          onValidate && onValidate(t("validations.required") || undefined);
        } else {
          setError("");
          onValidate && onValidate(undefined);
        }
      }
    }
  };

  return (
    <div className={classNames(Styles.radioGroupContainer, className)} ref={ref} onBlur={onBlur}>
      {title && <span className={Styles.title}>{title}</span>}
      <div
        role="radiogroup"
        className={classNames(Styles.radioGroup, Styles[direction], {
          [Styles.fullWidth]: fullWidth,
          [Styles.centered]: centered,
        })}
      >
        {/* This is needed because I need to pass some more props to the child component itself */}
        {React.Children.map(children, (child) => {
          // Checking isValidElement is the safe way and avoids a
          // typescript error too.
          if (React.isValidElement(child)) {
            return React.cloneElement(child as ReactElement, { error, resetError: () => setError("") });
          }
          return child;
        })}
      </div>
      {error && <div className={Styles.errorMessage}>{error}</div>}
    </div>
  );
};

type RadioProps = {
  children: React.ReactNode;
  selected?: boolean;
  name: string;
  value: string | number;
  onChange: (e: string | number) => void;
  onClick?: () => void;
  label?: string;
  width?: string;
  height?: string;
  scaleIcon?: boolean;
  withoutIcon?: boolean;
  smallPadding?: boolean;
  className?: string;
  autoFocus?: boolean;
  error?: string;
  resetError?: () => void;
};

const Radio = ({
  children,
  selected,
  name,
  value,
  onChange,
  onClick,
  label,
  width,
  height,
  withoutIcon = false,
  smallPadding = false,
  className,
  autoFocus,
  error,
  resetError,
}: RadioProps) => {
  const { primaryColor, primaryColorBorder } = useTheme();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const customWidth = width && width + "px";
  const customHeight = height && !label ? height + "px" : "";
  const customSize = { width: customWidth, height: customHeight };

  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  const valueChanged = (value: string | number) => {
    resetError && resetError();
    onChange(value);
  };

  const handleKeydown = (e: React.KeyboardEvent, value: string | number) => {
    // If the user presses enter or space, the radio button is selected
    if (e.key === "Enter" || e.key === " ") {
      valueChanged(value);
    }
  };

  return (
    <div
      style={customSize}
      className={classNames(Styles.radioWrapper, className, {
        [Styles.withLabel]: label,
      })}
    >
      <div
        className={classNames(Styles.radioButton, primaryColor, {
          [Styles.selected]: selected,
        })}
        onClick={() => {
          valueChanged(value);
          onClick && onClick();
        }}
      >
        <div
          className={classNames("radio-button", Styles.radioContent, {
            [Styles.onlyText]: withoutIcon,
            [Styles.smallPadding]: smallPadding,
            [primaryColorBorder]: hover,
            [Styles.error]: !!error,
            [Styles.focus]: focus,
          })}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          tabIndex={0}
          onKeyDown={(e) => handleKeydown(e, value)}
          ref={ref}
        >
          {children}
        </div>
        <input
          role="radio"
          id={name}
          aria-checked={selected}
          type="radio"
          name={name}
          value={value}
          defaultChecked={selected}
        />
      </div>
      {label && (
        <label htmlFor={name} className={Styles.label}>
          {label}
        </label>
      )}
    </div>
  );
};

export { Radio, RadioGroup };
